import React, { useEffect, useState } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { MenuIcon, BellIcon, XIcon, SparklesIcon, FileTextIcon, BookmarkIcon, MessageSquareIcon, BriefcaseIcon, ShoppingBagIcon, UserIcon, LifeBuoyIcon, LockIcon, LogOutIcon, PackageIcon } from 'lucide-react';
import { Logo } from './Logo';
import { RegionSelector } from './RegionSelector';
import { CartButton } from './cart/CartButton';
import { AuthButton } from './AuthButton';
import { useBusiness } from './BusinessContext';
import { useAuth } from './AuthContext';
type NavItem = {
  to: string;
  label: string;
  icon: React.ElementType;
  badge?: boolean;
};
export function MobileHeader() {
  const {
    hub
  } = useBusiness();
  const {
    isAuthenticated,
    openLogin,
    requireAuth,
    logout
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  // Close the drawer whenever the route changes.
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);
  // Lock body scroll while the drawer is open.
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [open]);
  const navItems: NavItem[] = [{
    to: '/',
    label: 'Discover',
    icon: SparklesIcon
  }, {
    to: '/requests',
    label: 'Requests',
    icon: FileTextIcon
  }, {
    to: '/saved',
    label: 'Saved',
    icon: BookmarkIcon
  }, {
    to: '/orders',
    label: 'My Orders',
    icon: PackageIcon
  }, {
    to: '/messages',
    label: 'Messages',
    icon: MessageSquareIcon
  }, {
    to: '/business',
    label: 'Business Hub',
    icon: BriefcaseIcon,
    badge: hub?.isNew
  }, ...(hub?.active ? [{
    to: `/shop/${hub.shopSlug || 'shop'}`,
    label: 'My Shop',
    icon: ShoppingBagIcon
  } as NavItem] : []), {
    to: '/profile',
    label: 'Profile',
    icon: UserIcon
  }];
  const handleNav = (to: string) => {
    setOpen(false);
    requireAuth(() => navigate(to));
  };
  return <>
      <header className="lg:hidden sticky top-0 z-40 bg-white border-b border-slate-200 px-4 py-3" data-id="element-2817" data-mp="245nleav8rtab">
        <div className="flex items-center justify-between" data-id="element-2818" data-mp="16khy3n6jrjo4">
          <div className="flex items-center gap-2" data-id="element-2819" data-mp="1bit4tp1jpe24">
            <button onClick={() => setOpen(true)} aria-label="Open menu" aria-expanded={open} className="p-2 -ml-2 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-2820" data-mp="1bjl0t0z21dsx">
              <MenuIcon className="w-5 h-5 text-slate-600" data-id="element-2821" data-mp="2rwt20s90aplo" />
            </button>
            <Logo size="sm" data-id="element-2822" data-mp="2wuukbaylykw3" />
          </div>
          <div className="flex items-center gap-2" data-id="element-2823" data-mp="2wtryhhg578n1">
            <CartButton data-id="element-2824" data-mp="l7ho5b2afbfh" />
            <RegionSelector compact data-id="element-2825" data-mp="l6pq7tl7x1m1" />
            <button aria-label="Notifications" className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-2826" data-mp="l6y7dxodit31">
              <BellIcon className="w-5 h-5 text-slate-600" data-id="element-2827" data-mp="bh650ha18vo0" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence data-id="element-2828" data-mp="6i1v1ofaatwm">
        {open && <div className="lg:hidden fixed inset-0 z-[60]" data-id="element-2829" data-mp="1js3dnsrqbfr">
            {/* Overlay */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setOpen(false)} className="absolute inset-0 bg-dark-navy/50 backdrop-blur-sm" data-id="element-2830" data-mp="1hfcum8y2sr3" />

            {/* Drawer */}
            <motion.aside initial={{
          x: '-100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '-100%'
        }} transition={{
          type: 'spring',
          stiffness: 320,
          damping: 34
        }} className="absolute top-0 left-0 h-full w-[82%] max-w-sm bg-white shadow-2xl flex flex-col" role="dialog" aria-modal="true" data-id="element-2831" data-mp="1muqsd2wf7huc">
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100" data-id="element-2832" data-mp="3s1qvnjw5ow80">
                <Logo size="sm" data-id="element-2833" data-mp="od3izo2mioa" />
                <button onClick={() => setOpen(false)} aria-label="Close menu" className="p-2 -mr-2 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors" data-id="element-2834" data-mp="omjrvjbdbgs">
                  <XIcon className="w-5 h-5" data-id="element-2835" data-mp="393epm70jk2gn" />
                </button>
              </div>

              <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto" data-id="element-2836" data-mp="3e280rsscvhdn">
                {navItems.map(item => {
              const locked = !isAuthenticated && item.to !== '/';
              if (locked) {
                return <button key={item.to} onClick={() => handleNav(item.to)} className="group w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-slate-400 hover:bg-slate-50 transition-all" data-id="element-2837" data-mp="2qgf9lykvfgh5">
                        <item.icon className="w-5 h-5" data-id="element-2838" data-mp="3ddz7zhvyuupe" />
                        <span className="flex-1 text-left" data-id="element-2839" data-mp="39dyj13b8r143">{item.label}</span>
                        <LockIcon className="w-4 h-4 text-slate-300" data-id="element-2840" data-mp="39bvvqr1h3m32" />
                      </button>;
              }
              return <NavLink key={item.to} to={item.to} onClick={() => setOpen(false)} className={({
                isActive
              }) => `relative flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all ${item.badge ? 'bg-primary-indigo/5 text-primary-indigo font-medium ring-1 ring-primary-indigo/30' : isActive ? 'bg-primary-indigo/5 text-primary-indigo font-medium' : 'text-slate-700 hover:bg-slate-50'}`} data-id="element-2841" data-mp="3i6k0mg34z1rc">
                      {({
                  isActive
                }) => <>
                          <item.icon className={`w-5 h-5 ${isActive || item.badge ? 'text-primary-indigo' : 'text-slate-400'}`} data-id="element-2842" data-mp="hsptsblwui17" />
                          <span className="flex-1" data-id="element-2843" data-mp="qprtc8ecd7wi">{item.label}</span>
                          {item.badge && <span className="text-[10px] font-bold uppercase tracking-wide bg-primary-indigo text-white px-2 py-0.5 rounded-full" data-id="element-2844" data-mp="vouydsubne4h">
                              New
                            </span>}
                        </>}
                    </NavLink>;
            })}

                {/* Help */}
                {isAuthenticated ? <NavLink to="/help" onClick={() => setOpen(false)} className={({
              isActive
            }) => `flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all ${isActive ? 'bg-primary-indigo/5 text-primary-indigo font-medium' : 'text-slate-700 hover:bg-slate-50'}`} data-id="element-2845" data-mp="2q75zp41y9e8s">
                    {({
                isActive
              }) => <>
                        <LifeBuoyIcon className={`w-5 h-5 ${isActive ? 'text-primary-indigo' : 'text-slate-400'}`} data-id="element-2846" data-mp="38vb0q3r82l3w" />
                        <span className="flex-1" data-id="element-2847" data-mp="2yyeq9shrjowp">Help</span>
                      </>}
                  </NavLink> : <button onClick={() => {
              setOpen(false);
              requireAuth(() => navigate('/help'));
            }} className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-slate-400 hover:bg-slate-50 transition-all" data-id="element-2848" data-mp="1nk18949oy3ol">
                    <LifeBuoyIcon className="w-5 h-5" data-id="element-2849" data-mp="248jwi8stdun1" />
                    <span className="flex-1 text-left" data-id="element-2850" data-mp="2893rp6ttuo6j">Help</span>
                    <LockIcon className="w-4 h-4 text-slate-300" data-id="element-2851" data-mp="289lu6l731ymw" />
                  </button>}
              </nav>

              {/* Auth action */}
              <div className="px-3 py-4 border-t border-slate-100" data-id="element-2852" data-mp="30y0wq8912ohc">
                {isAuthenticated ? <button onClick={() => {
              setOpen(false);
              logout();
              navigate('/');
            }} className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-error hover:bg-error/5 transition-colors" data-id="element-2853" data-mp="2n5bb44xevlkr">
                    <LogOutIcon className="w-5 h-5" data-id="element-2854" data-mp="3jqdb5tcyhg7u" />
                    <span className="flex-1 text-left font-medium" data-id="element-2855" data-mp="1888dch276n4z">
                      Log out
                    </span>
                  </button> : <AuthButton block onClick={() => {
              setOpen(false);
              openLogin();
            }} data-id="element-2856" data-mp="1hytjqqo44vyg" />}
              </div>
            </motion.aside>
          </div>}
      </AnimatePresence>
    </>;
}